/**
 * Activity, `/activity` and `/activity/:id` (slice 2c spec §8; Paper boards
 * 33, 34, 33b, 33c, 34b). Every turn of every agent: a dense table on a
 * desktop, two-line rows on a phone, filtered from the URL. A row opens its
 * receipt: a panel beside the table on a desktop (the table narrows), its own
 * screen on a phone. Owner only. Reached from Settings, its way back.
 */
import { BackLink } from "@crew/ui/components/back-link";
import { Button } from "@crew/ui/components/button";
import { AgentAvatar } from "@crew/ui/components/crew-avatar";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { FULL_HEIGHT_PAGE } from "@crew/ui/components/family-shell";
import { Spinner } from "@crew/ui/components/spinner";
import { PageTitle } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { ChevronDown, Search, SlidersHorizontal, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { useActivity, type ActivityItem, type ActivityPage } from "./api";
import {
  byDay,
  clock,
  duration,
  failure,
  rowState,
  startedBy,
  stepCounts,
  tokens,
  type DayGroup,
} from "./activity-view";
import {
  DEFAULT_FILTERS,
  filtersFrom,
  narrowed,
  RANGES,
  searchFor,
  STARTED_BY,
  toggled,
  USED,
  type Filters,
  type Range,
  type Status,
} from "./filters";
import { ReceiptView } from "./receipt";
import { AreaFilter } from "../areas/area-controls";
import { AreaChip } from "../areas/parts";
import { StartedByIcon, StatusMark, StepIcon } from "./icons";
import { HOME } from "../../app/home";

const RANGE_LABELS: Record<Range, string> = {
  today: "Today",
  week: "This week",
  month: "This month",
  all: "All time",
};

const STARTED_LABELS: Record<(typeof STARTED_BY)[number], string> = {
  person: "People",
  routine: "Routines",
  task: "Tasks",
  approval: "Approvals",
  nudge: "Nudges",
  retry: "Retries",
  unexpected: "Unexpected",
  handoff: "Handoffs",
};

const USED_LABELS: Record<(typeof USED)[number], string> = {
  web: "Web",
  files: "Files",
  tasks: "Tasks",
  docs: "Docs",
};

/** The route: members don't see Activity (spec §13, decision 2). */
export function ActivityRoute({ me }: { me: Me }) {
  if (me.user.role !== "owner") return <Navigate to={HOME} replace />;
  return <ActivityPageView me={me} />;
}

function useFilters(): [Filters, (next: Filters) => void, string] {
  const [search, setSearch] = useSearchParams();
  const filters = useMemo(() => filtersFrom(search), [search]);
  const set = (next: Filters) => setSearch(searchFor(next), { replace: true });
  return [filters, set, search.toString()];
}

function ActivityPageView({ me }: { me: Me }) {
  const { id } = useParams();
  const [filters, setFilters, query] = useFilters();
  const list = useActivity(filters);
  const pages = list.data?.pages ?? [];
  const first = pages[0];
  const items = pages.flatMap((p) => p.items);
  const days = pages.flatMap((p) => p.days);
  const tz = me.workspace.timezone;
  const groups = useMemo(() => byDay(items, days, tz), [items, days, tz]);
  const compact = Boolean(id);
  const suffix = query ? `?${query}` : "";

  return (
    <div className={FULL_HEIGHT_PAGE}>
      <section
        aria-label="Activity"
        className={cn("min-w-0 flex-1 flex-col overflow-y-auto", id ? "hidden md:flex" : "flex")}
      >
        <div className="flex flex-col gap-4 px-4 pt-6 pb-10 md:px-10 md:pt-9">
          <BackLink href="/settings">Settings</BackLink>
          <header className="flex flex-col gap-1">
            <PageTitle>Activity</PageTitle>
            <p className="text-body text-muted-foreground">
              Every time the crew worked: what it did, what it touched, and what it used.
            </p>
          </header>
          <Toolbar filters={filters} onChange={setFilters} page={first} me={me} compact={compact} />
          <ListBody
            list={list}
            empty={items.length === 0}
            filtered={narrowed(filters) || filters.status !== null || filters.area !== null}
            onClear={() => setFilters(DEFAULT_FILTERS)}
          >
            <ActivityList groups={groups} tz={tz} compact={compact} selected={id} suffix={suffix} />
          </ListBody>
          <p className="text-sm text-muted-foreground">
            Only you see Activity. Receipts are kept as long as the workspace is.
          </p>
        </div>
      </section>
      {id ? (
        <aside
          aria-label="Receipt"
          className="flex w-full min-w-0 flex-col md:w-[460px] md:shrink-0 md:border-l md:border-border"
        >
          <ReceiptView id={id} me={me} backTo={`/activity${suffix}`} />
        </aside>
      ) : null}
    </div>
  );
}

/** The list's state: loading, failed, empty, or the list and "Show older". */
function ListBody({
  list,
  empty,
  filtered,
  onClear,
  children,
}: {
  list: ReturnType<typeof useActivity>;
  empty: boolean;
  filtered: boolean;
  onClear: () => void;
  children: ReactNode;
}) {
  if (list.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  // A failed "Show older" keeps the pages already loaded: only a first page that failed replaces the list.
  if (!list.data)
    return <p className="py-10 text-body text-destructive">{errorHint(list.error)}</p>;
  if (empty) return <EmptyList filtered={filtered} onClear={onClear} />;
  return (
    <>
      {children}
      {list.isFetchNextPageError ? (
        <p className="text-sm text-destructive">{errorHint(list.error)}</p>
      ) : null}
      {list.hasNextPage ? (
        <Button
          variant="ghost"
          className="self-start"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {olderLabel(list)}
        </Button>
      ) : null}
    </>
  );
}

function olderLabel(list: ReturnType<typeof useActivity>): string {
  if (list.isFetchingNextPage) return "Loading…";
  return list.isFetchNextPageError ? "Try again" : "Show older";
}

// ── The toolbar: status tabs, totals, and filters ───────────────────────────

function Toolbar({
  filters,
  onChange,
  page,
  me,
  compact,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  page: ActivityPage | undefined;
  me: Me;
  compact: boolean;
}) {
  const agents = me.agents;
  const [sheet, setSheet] = useState(false);
  const setArea = (area: string | null) => onChange({ ...filters, area });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <StatusTabs filters={filters} onChange={onChange} counts={page?.counts.status} />
        {page && !compact ? (
          <div className="hidden shrink-0 items-center gap-5 text-sm text-muted-foreground lg:flex">
            <span>{totalsLine(page.totals)}</span>
            <Link href="/settings/usage" className="font-medium text-foreground underline">
              Usage and limits →
            </Link>
          </div>
        ) : null}
      </div>
      {page ? (
        // Phone (Paper 33b): the totals and Usage → under the tabs.
        <div className="flex items-center justify-between gap-3 text-[13px] text-muted-foreground md:hidden">
          <span className="truncate">{totalsLine(page.totals)}</span>
          <Link
            href="/settings/usage"
            className="-my-2 shrink-0 py-2 font-medium text-foreground underline"
          >
            Usage →
          </Link>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <SearchBox value={filters.q} onChange={(q) => onChange({ ...filters, q })} />
        <RangeMenu value={filters.range} onChange={(range) => onChange({ ...filters, range })} />
        {/* Slice 6d: the Area, a menu here on a desktop. */}
        <AreaFilter me={me} value={filters.area} onChange={setArea} className="hidden md:block" />
        <div className={cn("hidden items-center gap-2", compact ? "xl:flex" : "md:flex")}>
          <FacetMenus filters={filters} onChange={onChange} page={page} agents={agents} />
          {narrowed(filters) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...filters, ...clearFacets })}
            >
              Clear
            </Button>
          ) : null}
        </div>
        <Button
          variant="outline"
          className={cn(compact ? "xl:hidden" : "md:hidden")}
          onClick={() => setSheet(true)}
        >
          <SlidersHorizontal />
          Filter
        </Button>
      </div>
      {/* A phone's Area chips, under the search. */}
      <AreaFilter me={me} value={filters.area} onChange={setArea} className="md:hidden" />
      <FilterSheet
        open={sheet}
        onOpenChange={setSheet}
        filters={filters}
        onChange={onChange}
        page={page}
        agents={agents}
      />
    </div>
  );
}

const clearFacets = { agents: [], startedBy: [], used: [], q: "" };

/** "41m on the computer · 1.2M tokens · 71% cached". */
function totalsLine(t: ActivityPage["totals"]): string {
  const cached =
    t.tokens > 0 ? ` · ${Math.round((t.tokens_cache_read / t.tokens) * 100)}% cached` : "";
  return `${duration(t.computer_ms)} on the computer · ${tokens(t.tokens)} tokens${cached}`;
}

function StatusTabs({
  filters,
  onChange,
  counts,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  counts: ActivityPage["counts"]["status"] | undefined;
}) {
  const tabs: { value: Status | null; label: string; n?: number; mark?: ReactNode }[] = [
    { value: null, label: "All", n: counts?.all },
    {
      value: "working",
      label: "Working",
      n: counts?.working,
      mark: <span className="size-1.5 rounded-full bg-copper" />,
    },
    {
      value: "failed",
      label: "Failed",
      n: counts?.failed,
      mark: <span className="size-1.5 rounded-full bg-destructive" />,
    },
  ];
  if ((counts?.unexpected ?? 0) > 0 || filters.status === "unexpected") {
    tabs.push({
      value: "unexpected",
      label: "Unexpected",
      n: counts?.unexpected,
      mark: <TriangleAlert className="size-3 text-destructive" />,
    });
  }
  return (
    <div role="tablist" aria-label="Status" className="-mx-1 flex gap-1 overflow-x-auto px-1">
      {tabs.map((tab) => {
        const active = filters.status === tab.value;
        return (
          <button
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange({ ...filters, status: tab.value })}
            className={cn(
              "flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium md:rounded-md",
              active
                ? "bg-foreground text-background md:bg-card md:text-foreground md:shadow-[0_0_0_1px_rgb(31_42_68/0.06)]"
                : "text-copy ring-1 ring-input ring-inset md:ring-0 md:hover:bg-card/60",
            )}
          >
            {tab.mark}
            {tab.label}
            {tab.n !== undefined ? (
              <span className={cn("text-xs", active ? "opacity-70" : "text-muted-foreground")}>
                {tab.n}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** The search box; the URL follows a moment after typing stops. */
function SearchBox({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  // The URL changed from outside (a cleared filter, a link): show its search.
  // Adjusted while rendering, as React suggests, not in an effect.
  if (value !== seen) {
    setSeen(value);
    if (value !== draft.trim()) setDraft(value);
  }
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 300);
    return () => clearTimeout(timer);
  }, [draft, value, onChange]);
  return (
    <label className="relative flex min-w-0 flex-1 items-center md:max-w-72">
      <Search className="pointer-events-none absolute left-3 size-3.5 text-hint" />
      <Input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search asks, pages, files…"
        aria-label="Search activity"
        className="pl-8"
      />
    </label>
  );
}

function RangeMenu({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button variant="outline" className="shrink-0" />}>
        {RANGE_LABELS[value]}
        <ChevronDown className="text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as Range)}>
          {RANGES.map((r) => (
            <DropdownMenuRadioItem key={r} value={r}>
              {RANGE_LABELS[r]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Facet = {
  key: "agents" | "startedBy" | "used";
  label: string;
  options: { value: string; label: string; n: number }[];
};

/** The three facets with their options and counts (spec §7.1 `counts`). */
function facets(page: ActivityPage | undefined, agents: Me["agents"]): Facet[] {
  const counts = page?.counts;
  return [
    {
      key: "agents",
      label: "Agent",
      options: agents.map((a) => ({ value: a.slug, label: a.name, n: counts?.agent[a.slug] ?? 0 })),
    },
    {
      key: "startedBy",
      label: "Started by",
      // Nudges and handoffs show once there's one.
      options: STARTED_BY.filter(
        (k) => (k !== "nudge" && k !== "handoff") || (counts?.started_by[k] ?? 0) > 0,
      ).map((k) => ({ value: k, label: STARTED_LABELS[k], n: counts?.started_by[k] ?? 0 })),
    },
    {
      key: "used",
      label: "Used",
      options: USED.map((k) => ({ value: k, label: USED_LABELS[k], n: counts?.used[k] ?? 0 })),
    },
  ];
}

/** "All", the one chosen, or how many. */
function chosen(facet: Facet, picked: string[]): string {
  if (picked.length === 0) return "All";
  if (picked.length === 1) return facet.options.find((o) => o.value === picked[0])?.label ?? "1";
  return String(picked.length);
}

function FacetMenus({
  filters,
  onChange,
  page,
  agents,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  page: ActivityPage | undefined;
  agents: Me["agents"];
}) {
  return facets(page, agents).map((facet) => {
    const picked = filters[facet.key] as string[];
    return (
      <DropdownMenu key={facet.key}>
        <DropdownMenuTrigger render={<Button variant="outline" className="shrink-0" />}>
          <span className="text-muted-foreground">{facet.label}</span>
          {chosen(facet, picked)}
          <ChevronDown className="text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-56">
          {facet.options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={picked.includes(o.value)}
              onCheckedChange={() =>
                onChange({ ...filters, [facet.key]: toggled(picked, o.value) } as Filters)
              }
            >
              <span className="flex-1">{o.label}</span>
              <span className="text-xs text-hint">{o.n}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  });
}

/** The phone's filters (Paper 33c): the facets as chips, then "Show N turns". */
function FilterSheet({
  open,
  onOpenChange,
  filters,
  onChange,
  page,
  agents,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: Filters;
  onChange: (f: Filters) => void;
  page: ActivityPage | undefined;
  agents: Me["agents"];
}) {
  const shown = page?.counts.status[filters.status ?? "all"] ?? 0;
  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent showCloseButton className="pb-[env(safe-area-inset-bottom)]">
        <DrawerHeader className="flex-row items-center justify-between pr-14 pb-4">
          <DrawerTitle>Filter</DrawerTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ ...filters, ...clearFacets })}
          >
            Clear all
          </Button>
        </DrawerHeader>
        <div className="flex flex-col gap-5 px-4">
          {facets(page, agents).map((facet) => {
            const picked = filters[facet.key] as string[];
            return (
              <fieldset key={facet.key} className="flex flex-col gap-2">
                <legend className="mb-2 text-xs font-medium tracking-[0.06em] text-hint uppercase">
                  {facet.label}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {facet.options.map((o) => {
                    const on = picked.includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          onChange({ ...filters, [facet.key]: toggled(picked, o.value) } as Filters)
                        }
                        className={cn(
                          "flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium",
                          on
                            ? "bg-foreground text-background"
                            : "bg-card text-copy ring-1 ring-input ring-inset",
                        )}
                      >
                        {o.label}
                        <span className={cn("text-xs", on ? "opacity-70" : "text-hint")}>
                          {o.n}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
        <DrawerFooter className="pt-5">
          <Button size="lg" onClick={() => onOpenChange(false)}>
            Show {shown} {shown === 1 ? "turn" : "turns"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

// ── The list ────────────────────────────────────────────────────────────────

/** The desktop table's columns: full, or narrow beside a receipt (Paper 33, 34). */
const COLUMNS = {
  full: "grid-cols-[40px_140px_minmax(0,1fr)_150px_190px_56px_56px_16px]",
  compact: "grid-cols-[40px_18px_minmax(0,1fr)_auto_52px_16px]",
};

function ActivityList({
  groups,
  tz,
  compact,
  selected,
  suffix,
}: {
  groups: DayGroup[];
  tz: string;
  compact: boolean;
  selected: string | undefined;
  suffix: string;
}) {
  return (
    <div className="flex flex-col">
      {!compact ? (
        <div
          className={cn(
            "hidden h-7 items-center gap-3.5 border-b border-border px-2.5 text-[11px] font-medium tracking-[0.06em] text-hint uppercase md:grid",
            COLUMNS.full,
          )}
        >
          <span>Time</span>
          <span>Agent</span>
          <span>What</span>
          <span>Started by</span>
          <span>Steps</span>
          <span className="text-right">Tokens</span>
          <span className="text-right">Took</span>
          <span />
        </div>
      ) : null}
      {groups.map((group) => (
        <section key={group.date} aria-label={group.label} className="flex flex-col">
          <div className="flex items-baseline justify-between px-1 pt-4 pb-1.5 md:border-b md:border-border md:px-2.5">
            <h2 className="text-[11px] font-medium tracking-[0.06em] text-hint uppercase">
              {group.label}
            </h2>
            {group.day ? (
              <span className="text-xs text-hint">
                {group.day.turns} {group.day.turns === 1 ? "turn" : "turns"} ·{" "}
                {duration(group.day.computer_ms)}
                <span className="hidden md:inline"> · {tokens(group.day.tokens)} tokens</span>
              </span>
            ) : null}
          </div>
          <ul className="flex flex-col overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)] md:rounded-none md:bg-transparent md:shadow-none">
            {group.items.map((item) => (
              <li key={item.id}>
                <Row
                  item={item}
                  tz={tz}
                  compact={compact}
                  selected={selected === item.id}
                  href={`/activity/${item.id}${suffix}`}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Steps({ item }: { item: ActivityItem }) {
  const state = rowState(item);
  if (state === "working" && item.progress?.text) {
    return <span className="truncate text-xs text-copper">{item.progress.text}</span>;
  }
  if (state === "failed") {
    return (
      <span className="truncate text-xs text-destructive">
        {failure(item.fail_reason)}
        {item.can_retry ? " · Retry in the chat" : ""}
      </span>
    );
  }
  const counts = stepCounts(item.steps);
  return (
    <span className="flex items-center gap-2.5">
      {/* "No tools" only when it used none: hidden commands are still tools. */}
      {counts.length === 0 && item.steps_hidden === 0 ? (
        <span className="text-xs text-hint">No tools</span>
      ) : null}
      {counts.map(({ kind, n }) => (
        <span key={kind} className="flex items-center gap-1 text-xs text-copy">
          <StepIcon kind={kind} className="size-3 text-muted-foreground" />
          {n}
        </span>
      ))}
      {item.fallback ? (
        <span className="rounded bg-copper/10 px-1.5 text-[11px] text-copper">fallback</span>
      ) : null}
    </span>
  );
}

type RowParts = {
  item: ActivityItem;
  tz: string;
  state: ReturnType<typeof rowState>;
  glyph: ReactNode;
  title: ReactNode;
  started: ReactNode;
};

function rowParts(item: ActivityItem, tz: string): RowParts {
  const state = rowState(item);
  const red = state === "unexpected";
  return {
    item,
    tz,
    state,
    glyph: item.agent ? (
      <AgentAvatar glyph={item.agent.glyph} tint={agentTint(item.agent.tint)} size={18} />
    ) : (
      <span className="size-[18px]" />
    ),
    title: (
      <span className="flex min-w-0 items-center gap-2">
        <span className={cn("truncate font-medium", red ? "text-destructive" : "text-foreground")}>
          {item.title}
        </span>
        {/* Slice 6d (spec §4.4): its thread's Area. */}
        <AreaChip area={item.area} />
      </span>
    ),
    started: (
      <span
        className={cn(
          "flex min-w-0 items-center gap-1.5 text-xs",
          red ? "text-destructive" : "text-muted-foreground",
        )}
      >
        <StartedByIcon kind={item.started_by.kind} className="size-3 shrink-0" />
        <span className="truncate">{startedBy(item.started_by)}</span>
      </span>
    ),
  };
}

function Row({
  item,
  tz,
  compact,
  selected,
  href,
}: {
  item: ActivityItem;
  tz: string;
  compact: boolean;
  selected: boolean;
  href: string;
}) {
  const navigate = useNavigate();
  const parts = rowParts(item, tz);
  return (
    <Link
      href={href}
      data-activity-row
      aria-current={selected ? "true" : undefined}
      onClick={(e) => {
        // Keep the list's place: no scroll to top when a receipt opens.
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        void navigate(href, { preventScrollReset: true });
      }}
      className={cn(
        "block border-b border-border last:border-b-0 md:last:border-b",
        selected
          ? "bg-card md:rounded-md md:shadow-[0_0_0_1px_rgb(31_42_68/0.12)]"
          : "hover:bg-card/60",
      )}
    >
      {compact ? <CompactLine {...parts} /> : <DesktopLine {...parts} />}
      <PhoneLines {...parts} />
    </Link>
  );
}

/** Desktop: one line, in columns. */
function DesktopLine({ item, tz, state, glyph, title, started }: RowParts) {
  return (
    <span
      className={cn(
        "hidden h-[38px] items-center gap-3.5 px-2.5 text-[13px] md:grid",
        COLUMNS.full,
      )}
    >
      <span className="text-xs text-muted-foreground tabular-nums">{clock(item.at, tz)}</span>
      <span className="flex min-w-0 items-center gap-2">
        {glyph}
        <span className="truncate text-xs text-copy">{item.agent?.name ?? "—"}</span>
      </span>
      {title}
      {started}
      <span className="min-w-0 overflow-hidden">
        <Steps item={item} />
      </span>
      <span className="text-right text-xs text-muted-foreground">{tokens(item.tokens)}</span>
      <span className="text-right text-xs text-copy">{duration(item.computer_ms)}</span>
      <StatusMark state={state} />
    </span>
  );
}

/** Desktop beside a receipt: the narrower columns. */
function CompactLine({ item, tz, state, glyph, title, started }: RowParts) {
  return (
    <span
      className={cn(
        "hidden h-[38px] items-center gap-3.5 px-2.5 text-[13px] md:grid",
        COLUMNS.compact,
      )}
    >
      <span className="text-xs text-muted-foreground tabular-nums">{clock(item.at, tz)}</span>
      {glyph}
      {title}
      {started}
      <span className="text-right text-xs text-copy">{duration(item.computer_ms)}</span>
      <StatusMark state={state} />
    </span>
  );
}

/** Phone: two lines (Paper 33b). */
function PhoneLines({ item, tz, state, glyph, title, started }: RowParts) {
  return (
    <span className="flex items-start gap-2.5 px-3 py-2.5 md:hidden">
      <span className="pt-0.5">{glyph}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate">{title}</span>
          <span className="shrink-0 text-xs text-copy">{duration(item.computer_ms)}</span>
          <StatusMark state={state} />
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {clock(item.at, tz)}
          </span>
          {started}
          <span className="h-2.5 w-px shrink-0 bg-border" />
          <span className="min-w-0 overflow-hidden">
            <Steps item={item} />
          </span>
        </span>
      </span>
    </span>
  );
}

function EmptyList({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 py-12">
      <p className="text-body text-muted-foreground">
        {filtered
          ? "No turns match."
          : "Nothing yet. When the crew works, each turn shows up here."}
      </p>
      {filtered ? (
        <Button variant="outline" onClick={onClear}>
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}
